import React, { useMemo } from 'react';
import { PodMetricsMap } from '../utils/types';
import PodTable from './PodTable';
import { useAppSelector } from '@/common/reducers/hooks/store.hook';

interface PodHealthProps {
  pods: any[];
  podMetrics: PodMetricsMap;
  isLoadingMetrics: boolean;
  showGatewayNodes: boolean;
  showNormalNodes: boolean;
}

const PodHealth: React.FC<PodHealthProps> = ({
  pods,
  podMetrics,
  isLoadingMetrics,
  showGatewayNodes,
  showNormalNodes
}) => {
  // Get sessions directly from the Redux store
  const sessions = useAppSelector(state => state.DBSessionReducer.entities);
  const isLoadingSessions = useAppSelector(state => state.DBSessionReducer.itemsLoading);
  
  // Create our own podSessionMap
  const podSessionMap = useMemo(() => {
    const map = new Map<string, any[]>();
    
    // Get all sessions as an array
    const sessionArray = Object.values(sessions);
    if (!sessionArray.length) return map;
    
    // Get all existing pod IDs for quick lookup
    const existingPodIds = new Set(pods.map(pod => pod.metadata.name));
    
    // Only map sessions to pods that exist
    sessionArray.forEach(session => {
      if (session.podId && existingPodIds.has(session.podId)) {
        const podSessions = map.get(session.podId) || [];
        podSessions.push(session);
        map.set(session.podId, podSessions);
      }
    });
    
    return map;
  }, [sessions, pods]);

  // Get an appropriate empty message
  const emptyMessage = useMemo(() => {
    if (!showGatewayNodes && !showNormalNodes) {
      return "Please select at least one node type using the checkboxes above.";
    }
    
    if (pods.length === 0) {
      return "No pods found matching the current filter criteria.";
    }
    
    return "No pods found.";
  }, [pods.length, showGatewayNodes, showNormalNodes]);
  
  return (
    <PodTable 
      pods={pods}
      podMetrics={podMetrics}
      isLoadingMetrics={isLoadingMetrics}
      podSessionMap={podSessionMap}
      isLoadingSessions={isLoadingSessions}
      showDeployment={false}
      showNode={true}
      showApp={true}
      showCpu={true}
      showMemoryInfo={true}
      emptyMessage={emptyMessage}
      sortByMemory={true}
      showSearch={true}
      maxHeight="calc(100vh - 400px)"
    />
  );
};

export default PodHealth; 